import Https from 'https';
import { Constants, CYBER_CONSTANT } from 'constants';
import { IntercomProvider } from 'react-use-intercom';
import React from 'react';
import ReactDOM from 'react-dom';
import './index.css';
import { AppLoader } from '@genie-ui/react';
import { BrowserRouter } from 'react-router-dom';
import * as FullStory from '@fullstory/browser';
import { Provider } from 'react-redux';
import Layout from 'components/templates/layout';
import ConfigPath from 'configpath';
import ErrorPage from 'components/pages/errorpage';
import HeaderTop from 'components/molecules/header';
import FooterTop from 'components/molecules/footer';
import { fetchPPTXDownloadData, getDashboardDetails } from 'components/pages/cyber/api';
import store from 'stores';
import {
  setUser,
  setCurrentDashboardDetails,
  setAllDashboardDetails,
  setCohortSelection,
} from 'stores/slice';
import PPTX from 'components/pages/pptx/pptx';
import {
  setBenchmarkForKpiData,
  setKpiData,
  setMinimalBenchmarkData,
  setCurrentRR,
} from 'stores/slice/cyber';
import reportWebVitals from './reportWebVitals';

const isCyber = window?.location?.href?.includes(Constants.LABELS.CYBER);
const isInvitationUser = window?.location?.pathname?.includes(Constants.LABELS.INVITATION);
const isInvitationRoute = window?.location?.pathname?.includes(Constants.URLs.INVITATION);
const isCyberPPTXDownload = window?.location?.href?.includes(Constants.URLs.CYBER_PPTX_DOWNLOAD);

async function fetchUserDetails(data) {
  const root = ReactDOM.createRoot(document.getElementById('root'));
  const user = data?.body?.userDetails;
  if (isCyber) {
    await getDashboardDetails().then(
      (dashboard) => {
        if (dashboard?.body) {
          store.dispatch(setCurrentDashboardDetails(dashboard?.body?.currentDashboard));
          store.dispatch(setAllDashboardDetails(dashboard?.body?.dashboardList));
        }
      },
    );
  }

  let actisDataObj = {};
  const actisKey = isCyber
    ? Constants.LABELS.ACTIS_KEY_CYBER
    : Constants.LABELS.ACTIS_KEY_RAPID;

  FullStory.init({
    orgId: Constants.FULL_STORY_ID,
    devMode: !ConfigPath.IS_PROD,
  });

  store.dispatch(setUser(user));

  actisDataObj = {
    userId: user?.userId || '--',
    orgId: user?.orgId || '--',
    peopleCode: user?.peopleCode || '--',
    companyName: user?.companyName || '--',
    primaryEmail: user?.primaryEmail || '--',
    userType: user?.sotClassCode || '--',
    domain: user?.domain || '--',
    ...(isInvitationRoute && {
      invitationUser: isInvitationRoute.toString(),
    }),
    ...(isInvitationRoute && {
      eventName: Constants.EVENT_NAME,
    }),
  };

  const initializeFullStory = (email) => {
    FullStory.identify(user?.userId || email, {
      orgId_str: user?.orgId || '--',
      peopleCode_str: user?.peopleCode || '--',
      companyName_str: user?.companyName || '--',
      primaryEmail_str: user?.primaryEmail || '--',
      eventParticipantEmail: email,
      userType_str: user?.sotClassCode || '--',
      domain_str: user?.domain || '--',
      toolName_str: isCyber ? Constants.FS_CYBER : Constants.FS_DES,
      isGartnerAccount_bool: email?.includes(Constants.GARTNER_DOMAIN),
      ...(isInvitationRoute && {
        eventParticipantEmail: email,
      }),
      ...(isInvitationRoute && {
        invitationUser: isInvitationRoute.toString(),
      }),
      ...(isInvitationRoute && {
        eventName: Constants.EVENT_NAME,
      }),
    });
  };

  if (!isInvitationRoute) {
    initializeFullStory(user?.primaryEmailDec);
  }

  const goToGCOM = () => {
    window.location.href = ConfigPath.GARTNER_URL;
  };

  const handleShowIntercom = () => {
    window.pushEvent?.(
      {
        ...CYBER_CONSTANT.ACTIS.INTERCOM_ICON_OPEN,
        ...actisDataObj,
        activityValue: true,
      },
      Constants.LABELS.ACTIS_KEY_CYBER,
    );
  };

  const handleHideIntercom = () => {
    window.pushEvent?.(
      {
        ...CYBER_CONSTANT.ACTIS.INTERCOM_ICON_CLOSE,
        ...actisDataObj,
        activityValue: false,
      },
      Constants.LABELS.ACTIS_KEY_CYBER,
    );
  };

  const errorPage = () => {
    if (data?.status === Constants.ERROR_CODE.PERMISSION_DENIED) {
      return (
        <ErrorPage
          actisKey={actisKey}
          actisDataObj={actisDataObj}
          title={Constants.ACCESS_DENIED_ERROR.TITLE}
          body1={Constants.ACCESS_DENIED_ERROR.BODY1}
          body2={Constants.ACCESS_DENIED_ERROR.BODY2}
          label={Constants.ACCESS_DENIED_ERROR.LABEL}
          handleClick={goToGCOM}
        />
      );
    }
    return (
      <ErrorPage
        actisKey={actisKey}
        actisDataObj={actisDataObj}
      />
    );
  };

  root.render(
    <Provider store={store}>
      <IntercomProvider
        appId={CYBER_CONSTANT.INTERCOM.APP_ID}
        initializeDelay={CYBER_CONSTANT.INTERCOM.DELAY}
        onShow={handleShowIntercom}
        onHide={handleHideIntercom}
      >
        <BrowserRouter basename={Constants.URLs.BASE_URL}>
          <HeaderTop
            response={user}
            actisKey={actisKey}
            actisDataObj={actisDataObj}
            isCyber={isCyber}
          />
          {(data?.body || isInvitationRoute) ? (
            <Layout
              data={data?.body || {}}
              actisKey={actisKey}
              actisDataObj={actisDataObj}
              isCyber={isCyber}
              initializeFullStory={initializeFullStory}
            />
          )
            : errorPage()}
          <FooterTop
            actisKey={actisKey}
            actisDataObj={actisDataObj}
          />
        </BrowserRouter>
      </IntercomProvider>
    </Provider>,
  );
}

const apiUrl = () => {
  if (isCyber) {
    return Constants.URLs.CYBER_USER_API;
  }
  if (isInvitationUser) {
    return Constants.URLs.INVITATION_USER_API;
  }
  return Constants.URLs.DES_USER_API;
};

function renderPPTXDownload(data) {
  const root = ReactDOM.createRoot(document.getElementById('root'));
  const user = data?.body?.userDetails;
  store.dispatch(setUser(user));
  store.dispatch(setBenchmarkForKpiData(data?.body?.benchmarkData || {}));
  store.dispatch(setMinimalBenchmarkData(data?.body?.minimalBenchmark || {}));
  store.dispatch(setKpiData(data?.body?.kpiList));
  store.dispatch(setCurrentRR(data?.body?.recommendedResources || []));
  store.dispatch(setCurrentDashboardDetails(data?.body?.currentDashboard || {}));
  store.dispatch(setAllDashboardDetails(data?.body?.dashboardList || []));
  store.dispatch(setCohortSelection(data?.body?.cohortSelection || {}));
  root.render(
    <React.StrictMode>
      <Provider store={store}>
        <PPTX />
      </Provider>
    </React.StrictMode>,
  );
}

async function fetchData() {
  if (window.location.pathname.includes(Constants.URLs.CYBER_OLD)) {
    window.location.pathname = Constants.URLs.DATAHUB_DASHBOARD;
  }
  if (isCyberPPTXDownload) {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const userId = urlParams.get('userId');
      const token = urlParams.get('token');
      const apiResponse = await fetchPPTXDownloadData(userId, token);
      renderPPTXDownload(apiResponse);
    } catch (err) {
      <AppLoader />;
    }
  } else if (isInvitationRoute) {
    fetchUserDetails();
  } else {
    try {
      const apiResponse = await Https.get(ConfigPath.API_PATH
        ? (ConfigPath.API_PATH + apiUrl())
        : apiUrl());
      fetchUserDetails(apiResponse);
    } catch (error) {
      if (error?.status !== Constants.ERROR_CODE.UNAUTHORIZED) {
        fetchUserDetails(error);
      } else {
        <AppLoader />;
      }
    }
  }
}
fetchData();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
